<template>
  <div class="security-code">
    <base-heading underline>
      {{ $t('userAccount.securityCode.pageTitle') }}
    </base-heading>
    <div v-if="!this.$props.userInfo.securityCodeActive">
      <p>{{ $t('userAccount.securityCode.setCodeDescription') }}</p>
      <validation-observer ref="securityCodeObs"
                           tag="form"
                           class="code-form"
                           v-slot="{ errors }"
                           @submit.prevent="submitNewSecurityCode">
        <label for="security-1" class="code-label">{{ $t('userAccount.securityCode.enterCodeLabel') }}</label>
        <div class="code-inputs-wrapper">
          <validation-provider v-for="index in inputFieldsCount"
                               :key="'provider'+index"
                               tag="div"
                               class="code-input"
                               :mode="formSubmitted ? 'eager' : 'passive'"
                               v-slot="{ errors }"
                               rules="required|numeric|max:1">
            <base-input v-model="securityCode[index - 1]"
                        variant="center-grey"
                        :invalid="!!errors.length"
                        maxlength="1"
                        :ref="'input'+index"
                        :id="'security-'+index"
                        :name="'security-'+index"
                        @input="handleCodeInput(index, $event)"/>
          </validation-provider>
        </div>
        <p class="error-msg" v-html="formSubmitted && isFormInvalid(errors) ? $t('userAccount.securityCode.invalidCodeError') : '&nbsp;'"></p>
        <div class="action-button-wrapper">
          <base-button type="submit"
                       variant="primary"
                       ref="submitBtn"
                       :disabled="enteredCode.length < 6 || dataLoading">
            {{ $t('common.activate') }}
          </base-button>
        </div>
      </validation-observer>
    </div>
    <div v-else>
      <p>{{ $t('userAccount.securityCode.changeCodeDescription') }}</p>
      <validation-observer ref="securityCodeChangeObs"
                           tag="form"
                           class="code-form"
                           v-slot="{ errors }"
                           @submit.prevent="submitChangedSecurityCode">
        <label for="security-1" class="code-label">{{ $t('userAccount.securityCode.enterPreviousCodeLabel') }}</label>
        <div class="code-inputs-wrapper">
          <validation-provider v-for="index in newInputFieldsCount"
                               :key="'provider'+index"
                               tag="div"
                               class="code-input"
                               :mode="formSubmitted ? 'eager' : 'passive'"
                               v-slot="{ errors }"
                               rules="required|numeric|max:1">
            <base-input v-model="securityCode[index - 1]"
                        variant="center-grey"
                        :invalid="!!errors.length"
                        maxlength="1"
                        :ref="'input'+index"
                        :id="'security-'+index"
                        :name="'security-'+index"
                        @input="handleCodeInput(index, $event)"/>
          </validation-provider>
        </div>
        <label for="securityChange-1" class="code-label">{{ $t('userAccount.securityCode.enterNewCodeLabel') }}</label>
        <div class="code-inputs-wrapper">
          <validation-provider v-for="index in inputFieldsCount"
                               :key="'provider'+index"
                               tag="div"
                               class="code-input"
                               :mode="formSubmitted ? 'eager' : 'passive'"
                               v-slot="{ errors }"
                               rules="required|numeric|max:1">
            <base-input v-model="newSecurityCode[index - 1]"
                        variant="center-grey"
                        :invalid="!!errors.length"
                        maxlength="1"
                        :ref="'changeInput'+index"
                        :id="'securityChange-'+index"
                        :name="'securityChange-'+index"
                        @input="handleChangedCodeInput(index, $event)"/>
          </validation-provider>
        </div>
        <p class="error-msg" v-html="formSubmitted && isFormInvalid(errors) ? $t('userAccount.securityCode.invalidCodeError') : '&nbsp;'"></p>
        <div class="action-button-wrapper">
          <base-button type="submit"
                       variant="primary"
                       ref="submitBtn"
                       :disabled="enteredNewCode.length < 6 || dataLoading">
            {{ $t('userAccount.securityCode.changeCodeSubmitButton') }}
          </base-button>
        </div>
      </validation-observer>
    </div>
    <modal :visible="isNewCodeConfirmationModalShown"
           size="lg"
           :close-button-visible="false"
           prevent-backdrop-close>
      <div class="confirm-header">
        <img src="/assets/images/delete-warning.svg" alt="" class="warning-img">
        <h2>{{ $t('userAccount.securityCode.confirmModal.title') }}</h2>
      </div>
      <div class="confirm-body">
        <p class="description">
          {{ $t('userAccount.securityCode.confirmModal.description') }}</p>
        <div class="button-container">
          <base-button variant="primary" @click="postNewSecurityCode" :disabled="dataLoading">
            {{ $t('common.activate') }}
          </base-button>
          <button class="cancel-btn" @click="isNewCodeConfirmationModalShown = false">
            {{ $t('common.cancel') }}
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import errorMessageHandler from '@/mixins/errorMessageHandler'
import { removeUserToken } from '@/services/tokenService'
import codeInput from '@/mixins/codeInput'
import Modal from '@/components/common/modal/modal'
import logout from '@/mixins/logout'
import { addDataLayerEvent } from '@/services/dataLayer'

export default {
  name: 'user-security-code',
  components: { Modal },
  data () {
    return {
      inputFieldsCount: 6,
      newInputFieldsCount: 6,
      securityCode: [],
      newSecurityCode: [],
      formSubmitted: false,
      dataLoading: false,
      isNewCodeConfirmationModalShown: false
    }
  },
  mixins: [errorMessageHandler, codeInput, logout],
  props: {
    userInfo: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  computed: {
    enteredCode () {
      return this.securityCode.join('')
    },
    enteredNewCode () {
      return this.newSecurityCode.join('')
    }
  },
  methods: {
    handleChangedCodeInput (index, e) {
      // check if number, and if not don't allow input
      const numRegex = new RegExp('[^0-9]', 'g')
      if (numRegex.test(e)) {
        this.newSecurityCode[index - 1] = ''
        return
      }
      if (this.newSecurityCode[index - 1] && index < this.newInputFieldsCount) {
        this.getRefElement(this.$refs['changeInput' + (index + 1)]).focusIn()
      } else {
        setTimeout(() => {
          this.getRefElement(this.$refs.submitBtn).$el.focus()
        }, 10)
      }
    },
    async submitNewSecurityCode () {
      const formValid = await this.$refs.securityCodeObs.validate()
      this.formSubmitted = true

      if (formValid) {
        this.openNewSecurityCodeConfirmationModal()
      }
    },
    openNewSecurityCodeConfirmationModal () {
      this.isNewCodeConfirmationModalShown = true
    },
    postNewSecurityCode () {
      this.dataLoading = true
      this.$apiRequest.post('user/securitycode', { code: this.enteredCode })
        .then(response => {
          this.toast('success', {
            title: this.$t('userAccount.securityCode.setCodeSuccessTitle'),
            message: this.$t('userAccount.securityCode.setCodeSuccessMessage')
          })
          addDataLayerEvent('security_code_activation')
          removeUserToken()
          setTimeout(() => {
            this.logoutUser()
          }, 3000)
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
          this.dataLoading = false
        })
    },
    async submitChangedSecurityCode () {
      const formValid = await this.$refs.securityCodeChangeObs.validate()
      this.formSubmitted = true

      if (formValid) {
        this.dataLoading = true
        this.postChangedSecurityCode()
      } else {
        this.dataLoading = false
      }
    },
    postChangedSecurityCode () {
      this.$apiRequest.put('user/securitycode', { oldCode: this.enteredCode, newCode: this.enteredNewCode })
        .then(response => {
          this.toast('success', {
            title: this.$t('userAccount.securityCode.changeCodeSuccessTitle'),
            message: this.$t('userAccount.securityCode.setCodeSuccessMessage')
          })
          removeUserToken()
          setTimeout(() => {
            // this.$emit('refresh', { type: 'userInfo' })
            window.location.reload()
          }, 3000)
        })
        .catch(e => {
          if (e.response.status === 400) {
            // handle code error
            const errorsObj = {}
            Object.keys(this.$refs.securityCodeChangeObs.fields).forEach(key => {
              errorsObj[key] = 'error'
            })
            this.$refs.securityCodeChangeObs.setErrors(errorsObj)
          } else {
            this.handleErrorMessage(e.response.data)
          }
        })
        .finally(() => {
          this.dataLoading = false
        })
    }
  },
  mounted () {
    this.$nextTick(() => {
      const firstInput = this.getRefElement(this.$refs.input1).$refs.input
      firstInput.addEventListener('paste', this.handleKeyboardPaste)
    })
  },
  beforeDestroy () {
    const firstInput = this.getRefElement(this.$refs.input1).$refs.input
    firstInput.removeEventListener('paste', this.handleKeyboardPaste)
  }
}
</script>

<style scoped lang="scss" src="./styles/security-code.scss"></style>
